import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import { fadeInUp, stagger } from './data';

/* ─── Final CTA ─── */
export const CTASection = ({ user }) => (
  <section className="py-20 md:py-32">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-6 text-white">
          Ready to Modernize Your Operation?
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10">
          Join hundreds of slot machine operators who trust SlotFleet to manage their
          inventory, track collections, and grow their business.
        </motion.p>
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to={user ? '/dashboard' : '/signup'}>
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold px-10 h-14 text-lg shadow-[0_0_30px_rgba(245,158,11,0.3)]"
              data-testid="final-cta"
            >
              {user ? 'Go to Dashboard' : 'Start Your Free Trial'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          {!user && (
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-400 px-10 h-14 text-lg"
              >
                Login
              </Button>
            </Link>
          )}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─── About ─── */
const ABOUT_VALUES = [
  { title: 'Operator-First', desc: 'Every feature starts with a real problem faced by amusement game operators.' },
  { title: 'Security & Privacy', desc: 'Enterprise-grade security with MFA, RBAC, WAF, encrypted data, and audit trails.' },
  { title: 'Simplicity', desc: 'Powerful features in an intuitive interface. If it takes more than 3 clicks, we redesign it.' },
  { title: 'Partnership', desc: 'We succeed when our customers succeed. Dedicated onboarding and support for every operator.' },
];

export const AboutSection = () => (
  <section id="about" aria-label="About SlotFleet" className="py-20 md:py-32 bg-neutral-900/50">
    <div className="max-w-5xl mx-auto px-4">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-4">About SlotFleet</motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg max-w-3xl mx-auto">We're building the operating system for the amusement games industry — so operators can focus on growing their business, not wrestling with spreadsheets.</motion.p>
      </motion.div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {ABOUT_VALUES.map((v, i) => (
          <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="bg-black border border-amber-500/10 rounded-xl p-6">
            <h3 className="font-semibold text-amber-400 mb-2">{v.title}</h3>
            <p className="text-gray-400 text-sm">{v.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Blog ─── */
const BLOG_POSTS = [
  { title: 'How to Manage a Skill Games Business: Complete Guide', category: 'Guide', time: '8 min',
    content: 'Running a skill games operation involves juggling locations, machines, collections, and partner payouts. Here\'s how modern software can automate 80% of your daily tasks.\n\nKey areas to automate:\n\n1. **Collections Tracking** — Record money in/out per machine with period, lifetime, or mothergoose collection types. Upload receipt images and auto-calculate net profit with split agreements.\n\n2. **Inventory Management** — Track every cabinet, game board, and part with serial numbers. Transfer machines between locations with full audit trail.\n\n3. **Location Portal** — Give venue owners their own login for bookkeeping, employee management, service calls, and daily reports.\n\n4. **Partner Payouts** — Set ownership percentages and let the system calculate distributions automatically. Negative balances carry forward.\n\n5. **Route Planning** — Use Google Maps to plan efficient collection routes. Drag-to-reorder stops and assign drivers.\n\nThe right software pays for itself within the first month by eliminating spreadsheet errors and missed collections.' },
  { title: '5 Accounting Mistakes That Cost Operators Thousands', category: 'Finance', time: '6 min',
    content: 'Manual spreadsheets, missed collections, and incorrect partner splits are costing operators money every month.\n\n**Mistake #1: Manual Spreadsheets**\nSpreadsheets are error-prone. One wrong formula can cascade across months of financial data. Automated accounting eliminates this risk.\n\n**Mistake #2: Missed Collections**\nWithout a system tracking collection dates, locations can go weeks without being serviced. Automated scheduling and notifications ensure nothing falls through the cracks.\n\n**Mistake #3: Incorrect Split Calculations**\nPartner splits calculated by hand often have errors. Software calculates to the penny based on your agreements.\n\n**Mistake #4: No Receipt Documentation**\nWithout photos of collections, disputes become he-said-she-said. Requiring image uploads creates an indisputable record.\n\n**Mistake #5: Ignoring Negative Balances**\nWhen a location runs negative, the balance should carry forward. Many operators forget this and lose money on the next collection.' },
  { title: 'Why Every Location Needs a Digital Portal', category: 'Product', time: '5 min',
    content: 'Paper logbooks and phone calls are slowing down your operations. A digital location portal transforms the relationship between operators and venue owners.\n\n**What a Location Portal Provides:**\n\n- **Daily Bookkeeping** — Shift-based machine readings with in/out tracking per machine. Support for period, lifetime, and mothergoose collection types.\n\n- **Employee Management** — Create employee accounts with shift assignments. Employees can only access bookkeeping for their assigned shift.\n\n- **Service Calls** — Venue owners submit maintenance requests with priority levels. Your admin team gets instant email and in-app notifications.\n\n- **Reports** — Real-time access to credits, over/short calculations, and collection history.\n\n- **Independence** — With the Location Plan ($29.99/mo), venue owners can manage their own portal without being tied to a company account.\n\nThe result: fewer phone calls, faster issue resolution, and happier location partners.' },
];

export const BlogSection = () => {
  const [expandedBlog, setExpandedBlog] = useState(null);

  return (
    <section id="blog" aria-label="Blog" className="py-20 md:py-32">
      <div className="max-w-5xl mx-auto px-4">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-4">From Our Blog</motion.h2>
          <motion.p variants={fadeInUp} className="text-gray-400 text-lg">Insights and best practices for amusement games operators.</motion.p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {BLOG_POSTS.map((post, i) => (
            <motion.div key={post.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-amber-500/30 transition-colors cursor-pointer group"
              onClick={() => setExpandedBlog(i)}>
              <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded">{post.category}</span>
              <h3 className="font-semibold mt-3 mb-2 group-hover:text-amber-400 transition-colors">{post.title}</h3>
              <p className="text-xs text-gray-500 mb-3">{post.time} read</p>
              <span className="text-xs text-amber-500">Read more →</span>
            </motion.div>
          ))}
        </div>

        {/* Blog Post Modal */}
        {expandedBlog !== null && (() => {
          const post = BLOG_POSTS[expandedBlog];
          return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setExpandedBlog(null)}>
              <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-neutral-900 border border-neutral-700 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 px-6 py-4 flex items-center justify-between z-10">
                  <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded">{post.category}</span>
                  <button onClick={() => setExpandedBlog(null)} className="text-gray-400 hover:text-white p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="px-6 py-6">
                  <h2 className="text-2xl font-bold mb-2">{post.title}</h2>
                  <p className="text-xs text-gray-500 mb-6">{post.time} read</p>
                  <div className="space-y-4">
                    {post.content.split('\n\n').map((para, j) => (
                      <p key={j} className="text-sm text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </div>
    </section>
  );
};

/* ─── Careers ─── */
const JOBS = [
  { title: 'Full Stack Developer', type: 'Remote (US)', tag: 'Engineering' },
  { title: 'Customer Success Manager', type: 'Remote', tag: 'Support' },
  { title: 'Sales Representative', type: 'Remote', tag: 'Sales' },
  { title: 'Mobile Developer', type: 'Remote', tag: 'Engineering' },
];

export const CareersSection = () => (
  <section id="careers" aria-label="Careers" className="py-20 md:py-32 bg-neutral-900/50">
    <div className="max-w-5xl mx-auto px-4">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-4">Join Our Team</motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg">We're hiring! Join a fast-moving team solving real problems for amusement game operators.</motion.p>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {JOBS.map((job) => (
          <motion.a key={job.title} href="mailto:careers@slotfleet.com" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="flex items-center justify-between bg-black border border-neutral-800 rounded-xl p-5 hover:border-amber-500/30 transition-colors group">
            <div>
              <h3 className="font-semibold group-hover:text-amber-400 transition-colors">{job.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{job.type}</p>
            </div>
            <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded">{job.tag}</span>
          </motion.a>
        ))}
      </div>
      <p className="text-center text-gray-400 text-sm mt-6">Send your resume to <a href="mailto:careers@slotfleet.com" className="text-amber-400 hover:underline">careers@slotfleet.com</a></p>
    </div>
  </section>
);

/* ─── Contact ─── */
const CONTACTS = [
  { label: 'General', email: 'info@slotfleet.com', desc: 'General inquiries' },
  { label: 'Sales', email: 'sales@slotfleet.com', desc: 'Pricing & partnerships' },
  { label: 'Support', email: 'support@slotfleet.com', desc: 'Technical help' },
];

export const ContactSection = () => (
  <section id="contact" aria-label="Contact Us" className="py-20 md:py-32">
    <div className="max-w-5xl mx-auto px-4">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-4">Get in Touch</motion.h2>
        <motion.p variants={fadeInUp} className="text-gray-400 text-lg">Questions? We'd love to hear from you.</motion.p>
      </motion.div>
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {CONTACTS.map((c) => (
            <motion.a key={c.email} href={`mailto:${c.email}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="block bg-neutral-900 border border-neutral-800 rounded-xl p-5 hover:border-amber-500/30 transition-colors">
              <h3 className="font-semibold text-amber-400 mb-1">{c.label}</h3>
              <p className="text-sm text-white">{c.email}</p>
              <p className="text-xs text-gray-500 mt-1">{c.desc}</p>
            </motion.a>
          ))}
          <p className="text-gray-400 text-sm pt-2">United States | Mon-Fri 9 AM - 6 PM EST</p>
        </div>
        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-3">
          <form onSubmit={(e) => { e.preventDefault(); toast.success('Message sent! We\'ll get back to you within 24 hours.'); e.target.reset(); }} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 space-y-4">
            <h3 className="font-semibold text-lg mb-2">Send us a message</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Name *</label>
                <input required name="name" placeholder="Your name" className="w-full bg-black border border-neutral-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none" />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Email *</label>
                <input required type="email" name="email" placeholder="you@company.com" className="w-full bg-black border border-neutral-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Subject</label>
              <input name="subject" placeholder="What's this about?" className="w-full bg-black border border-neutral-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none" />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Message *</label>
              <textarea required name="message" rows={4} placeholder="Tell us how we can help..." className="w-full bg-black border border-neutral-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-amber-500 focus:outline-none resize-none" />
            </div>
            <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-2.5 rounded-lg transition-colors">
              Send Message
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  </section>
);
